// A12 — Add/Edit Job. Same TVP2 §9 styling as AdminUserEdit: bordered
// Paper card, MUI standard inputs, Bootstrap-blue Save / grey Reset.
//
// Add mode  — POST /v1/admin/jobs (customer_id + address_id + category_id +
//             required_qualification + title + description all required by
//             the wire schema).
// Edit mode — GET /v1/admin/jobs/:id, then PATCH the diff. PatchAdminJobReq
//             only allows status / title / description / price / engineer +
//             notes, so address / category / qualification are RENDERED
//             read-only in edit mode (deliberate API design — re-creating
//             the job is the path for a deep change).

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  PlayCircleOutline as StartIcon,
  CheckCircleOutline as EndIcon,
  PinDrop as PinDropIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { ApiError, apiFetch } from "@/lib/api";
import { useToast } from "@/components/Toast";

interface AddressRow {
  id: string;
  label: string | null;
  street: string;
  apt: string | null;
  district: string | null;
  region: string | null;
}
interface CategoryRow {
  id: string;
  name: string;
}
interface PhotoRow {
  id: string;
  kind: string;
  storage_path: string;
  signed_url: string | null;
  created_at: string;
}
interface LocationPing {
  id: number;
  lat: number;
  lng: number;
  accuracy_m: number | null;
  pinged_at: string;
}
interface JobDetail {
  job: {
    id: string;
    customer_id: string;
    title: string;
    description: string | null;
    status: string;
    price_amount: number | null;
    address_id: string | null;
    category_id: string | null;
    required_qualification: string | null;
    // Captured by the engineer mobile app at "I've arrived" /
    // "Submit completion" taps. Latitude / longitude are decimal degrees;
    // service_started_at / service_completed_at are timestamptz strings.
    service_started_lat: number | null;
    service_started_lng: number | null;
    service_started_at: string | null;
    service_completed_lat: number | null;
    service_completed_lng: number | null;
    service_completed_at: string | null;
  };
  customer: {
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  address: AddressRow | null;
  category: CategoryRow | null;
  photos: PhotoRow[];
  location_pings: LocationPing[];
}

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "accepted", label: "Accepted" },
  { value: "travelling", label: "Travelling" },
  { value: "on_site", label: "On Site" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];
const QUAL_OPTIONS = [
  { value: "primary", label: "Primary" },
  { value: "senior", label: "Senior" },
  { value: "expert", label: "Expert" },
];

interface FormState {
  title: string;
  description: string;
  status: string;
  price_amount: string;
  customer_id: string;
  address_id: string;
  category_id: string;
  required_qualification: string;
}

function friendlyError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : fallback;
  }
  const code =
    (err.body && typeof err.body === "object" && "error" in err.body
      ? String((err.body as { error?: unknown }).error ?? "")
      : "") || err.message;
  switch (code) {
    case "job_not_found":
      return "Job not found.";
    case "bad_request":
      return "Invalid input. Double-check the form and try again.";
    case "db_error":
      return "Database error. Try again in a moment.";
    default:
      return err.message || fallback;
  }
}

const formatAddress = (a: AddressRow | null): string => {
  if (!a) return "";
  const parts = [
    a.label ? `[${a.label}]` : null,
    a.street,
    a.apt,
    a.district,
    a.region,
  ].filter(Boolean);
  return parts.join(", ");
};

export function JobForm() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id) && id !== "add";
  const { show, ToastEl } = useToast();

  const detail = useQuery<JobDetail>({
    queryKey: ["jobs", id],
    queryFn: () => apiFetch<JobDetail>(`/v1/admin/jobs/${id}`),
    enabled: isEdit,
  });

  const blank: FormState = {
    title: "",
    description: "",
    status: "published",
    price_amount: "",
    customer_id: "",
    address_id: "",
    category_id: "",
    required_qualification: "primary",
  };

  const initialFromDetail = (d: JobDetail): FormState => {
    const j = d.job;
    return {
      title: j.title,
      description: j.description ?? "",
      status: j.status,
      price_amount: j.price_amount != null ? String(j.price_amount) : "",
      customer_id: j.customer_id,
      address_id: j.address_id ?? "",
      category_id: j.category_id ?? "",
      required_qualification: j.required_qualification ?? "primary",
    };
  };

  const [form, setForm] = useState<FormState>(blank);
  useEffect(() => {
    if (detail.data) setForm(initialFromDetail(detail.data));
  }, [detail.data]);

  const original = useMemo<FormState>(
    () => (detail.data ? initialFromDetail(detail.data) : blank),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detail.data],
  );

  // Categories — needed in BOTH modes (read-only label in edit, picker in
  // add). Tiny list; no pagination concern.
  const categoriesQ = useQuery<{ items: CategoryRow[] }>({
    queryKey: ["job-categories"],
    queryFn: () => apiFetch("/v1/admin/job-categories?limit=100"),
  });
  const categories = categoriesQ.data?.items ?? [];

  // Addresses for the SELECTED customer — only useful in add mode (edit mode
  // shows the embedded address from /jobs/:id read-only).
  const addressesQ = useQuery<{
    addresses: AddressRow[];
  }>({
    queryKey: ["app-users", form.customer_id, "addresses"],
    queryFn: () =>
      apiFetch(`/v1/admin/app-users/${form.customer_id}`).then(
        (d) => d as { addresses: AddressRow[] },
      ),
    enabled: !isEdit && Boolean(form.customer_id),
  });
  const customerAddresses = addressesQ.data?.addresses ?? [];

  // Photo upload (edit mode). Two-step:
  //   1. POST /v1/admin/jobs/:id/photos → server inserts the job_photos row
  //      and returns a Supabase Storage signed-upload URL.
  //   2. PUT the file directly to that URL — bypasses the Worker for the
  //      payload so big images don't burn CPU time.
  // After all selected files are processed we refetch /jobs/:id so the new
  // signed view URLs render. If upload fails the row is orphaned in DB; the
  // daily storage cron (sub-plan 08 §8.2.d) sweeps it.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const uploadFiles = async (files: FileList) => {
    if (!id || files.length === 0) return;
    setUploading(true);
    let okCount = 0;
    let failCount = 0;
    try {
      for (const file of Array.from(files)) {
        try {
          const handoff = (await apiFetch(`/v1/admin/jobs/${id}/photos`, {
            method: "POST",
            body: {},
          })) as { id: string; path: string; signed_url: string };
          const put = await fetch(handoff.signed_url, {
            method: "PUT",
            headers: { "content-type": file.type || "image/jpeg" },
            body: file,
          });
          if (!put.ok) throw new Error(`storage_put_${put.status}`);
          okCount += 1;
        } catch {
          failCount += 1;
        }
      }
    } finally {
      setUploading(false);
      detail.refetch();
      if (fileInputRef.current) fileInputRef.current.value = "";
      show(
        failCount === 0
          ? `Uploaded ${okCount} photo${okCount === 1 ? "" : "s"}`
          : `Uploaded ${okCount}; ${failCount} failed`,
        failCount === 0 ? "success" : "warning",
      );
    }
  };

  const deletePhoto = useMutation({
    mutationFn: (photoId: string) =>
      apiFetch(`/v1/admin/jobs/${id}/photos/${photoId}`, { method: "DELETE" }),
    onSuccess: () => {
      show("Photo removed", "success");
      detail.refetch();
    },
    onError: (err) => show(friendlyError(err, "Delete failed"), "error"),
  });

  const save = useMutation({
    mutationFn: async () => {
      const priceNum = form.price_amount ? Number(form.price_amount) : undefined;
      if (isEdit) {
        // PatchAdminJobReq allows status/title/description/price/engineer/notes.
        // Address / category / qualification are NOT editable post-creation.
        const body: Record<string, unknown> = {};
        if (form.title !== original.title) body.title = form.title;
        if (form.description !== original.description)
          body.description = form.description;
        if (form.status !== original.status) body.status = form.status;
        if (
          priceNum != null &&
          !Number.isNaN(priceNum) &&
          String(priceNum) !== original.price_amount
        )
          body.price_amount = priceNum;
        if (Object.keys(body).length === 0) {
          show("No changes to save", "info");
          return;
        }
        await apiFetch(`/v1/admin/jobs/${id}`, { method: "PATCH", body });
      } else {
        const body: Record<string, unknown> = {
          customer_id: form.customer_id,
          address_id: form.address_id,
          category_id: form.category_id,
          required_qualification: form.required_qualification,
          title: form.title,
          description: form.description,
        };
        if (priceNum != null && !Number.isNaN(priceNum)) body.price_amount = priceNum;
        await apiFetch("/v1/admin/jobs", { method: "POST", body });
      }
    },
    onSuccess: () => {
      show(isEdit ? "Job updated" : "Job published", "success");
      navigate("/app/jobs");
    },
    onError: (err) => show(friendlyError(err, "Save failed"), "error"),
  });

  if (isEdit && detail.isLoading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isEdit && detail.isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load job</Typography>
      </Box>
    );
  }

  const customer = detail.data?.customer;
  const customerName = customer
    ? [customer.first_name, customer.last_name].filter(Boolean).join(" ") ||
      customer.display_name ||
      ""
    : "";
  const editAddress = detail.data?.address ?? null;
  const editCategory = detail.data?.category ?? null;
  const photos = detail.data?.photos ?? [];
  const locationPings = detail.data?.location_pings ?? [];
  const jobRow = detail.data?.job;
  const startLat = jobRow?.service_started_lat ?? null;
  const startLng = jobRow?.service_started_lng ?? null;
  const startAt = jobRow?.service_started_at ?? null;
  const endLat = jobRow?.service_completed_lat ?? null;
  const endLng = jobRow?.service_completed_lng ?? null;
  const endAt = jobRow?.service_completed_at ?? null;
  const hasAnyLocationData =
    startLat != null ||
    endLat != null ||
    locationPings.length > 0;

  const cardSx = {
    border: `1px solid ${tokens.colors.admin.border}`,
    borderRadius: 1,
    p: 3,
  };
  const sectionHeading = {
    fontSize: tokens.font.sizes.body,
    fontWeight: tokens.font.weights.semibold,
    color: tokens.colors.admin.textHeading,
    mb: 2,
  } as const;
  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const primaryBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#7BB1F0",
      color: "rgba(255,255,255,0.85)",
    },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
  };

  // Add-mode validity: every wire-required field must be present.
  const addValid =
    Boolean(form.customer_id) &&
    Boolean(form.address_id) &&
    Boolean(form.category_id) &&
    Boolean(form.required_qualification) &&
    form.title.trim().length > 0 &&
    form.description.trim().length > 0;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Admin-created jobs are intentionally disabled. The /jobs/add
            route exists per the legacy spec (sub-plan 13 §13.4 — must
            register a /jobs/add slot in the router) but the platform's
            actual job-creation flow runs through the mobile app's
            customer flow (apps/mobile/app/(customer)/new-job.tsx →
            POST /v1/customer/jobs). Creating a job here would skip the
            captcha, OTP-verification, payment-hold, and address-coord
            preflight that the mobile flow runs — the resulting row
            would have no held funds and no engineer-recommendation
            seed, leaving the FSM in an unrecoverable state. */}
        {!isEdit && (
          <Alert severity="error" variant="filled" sx={{ borderRadius: 1 }}>
            Adding jobs from the admin console is disabled. Jobs must be
            created from the customer's mobile app. The form below is
            kept visible for reference only — the Publish button is
            permanently disabled on this page.
          </Alert>
        )}
        {/* Customer + Address card (Customer info read-only in edit mode;
            picker pair in add mode). */}
        <Paper elevation={0} sx={cardSx}>
          <Typography sx={sectionHeading}>Customer & Location</Typography>
          {isEdit ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
                gap: 3,
              }}
            >
              <TextField
                variant="standard"
                fullWidth
                label="Customer Name"
                disabled
                value={customerName}
              />
              <TextField
                variant="standard"
                fullWidth
                label="E-mail"
                disabled
                value={customer?.email ?? ""}
              />
              <TextField
                variant="standard"
                fullWidth
                label="Phone"
                disabled
                value={customer?.phone ?? ""}
              />
              <Box sx={{ gridColumn: { xs: "auto", md: "span 4" } }}>
                <TextField
                  variant="standard"
                  fullWidth
                  label="Address"
                  disabled
                  value={formatAddress(editAddress)}
                />
              </Box>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 3,
                  mb: 3,
                }}
              >
                <CustomerPicker
                  value={form.customer_id}
                  onChange={(id) =>
                    setForm({ ...form, customer_id: id, address_id: "" })
                  }
                />
                <TextField
                  variant="standard"
                  fullWidth
                  required
                  select
                  label="Address"
                  helperText={
                    !form.customer_id
                      ? "Pick a customer first"
                      : customerAddresses.length === 0
                        ? addressesQ.isFetching
                          ? "Loading addresses…"
                          : "Customer has no saved addresses"
                        : "Choose where the engineer should go"
                  }
                  disabled={!form.customer_id || addressesQ.isFetching}
                  InputLabelProps={{ shrink: true }}
                  value={form.address_id}
                  onChange={(e) =>
                    setForm({ ...form, address_id: e.target.value })
                  }
                >
                  {customerAddresses.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {formatAddress(a)}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </>
          )}
        </Paper>

        {/* Job details card */}
        <Paper elevation={0} sx={cardSx}>
          <Typography sx={sectionHeading}>Job Details</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              variant="standard"
              fullWidth
              required
              select
              label="Category"
              value={isEdit ? editCategory?.id ?? "" : form.category_id}
              disabled={isEdit}
              helperText={isEdit ? "Set at job creation — not editable" : " "}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setForm({ ...form, category_id: e.target.value })}
            >
              {(isEdit && editCategory
                ? [editCategory]
                : categories
              ).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              variant="standard"
              fullWidth
              required
              select
              label="Required Qualification"
              value={form.required_qualification}
              disabled={isEdit}
              helperText={isEdit ? "Set at job creation — not editable" : " "}
              onChange={(e) =>
                setForm({ ...form, required_qualification: e.target.value })
              }
            >
              {QUAL_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              variant="standard"
              fullWidth
              required
              select
              label="Status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              variant="standard"
              fullWidth
              label="Price (HKD)"
              type="number"
              value={form.price_amount}
              onChange={(e) =>
                setForm({ ...form, price_amount: e.target.value })
              }
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <TextField
              variant="standard"
              fullWidth
              required
              label="Title"
              inputProps={{ maxLength: 200 }}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <TextField
              variant="standard"
              fullWidth
              required
              multiline
              minRows={4}
              label="Job Content"
              inputProps={{ maxLength: 4000 }}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </Box>
          <Stack direction="row" spacing={2}>
            <Tooltip
              title={
                !isEdit
                  ? "Adding jobs from the admin console is disabled. Create the job from the customer's mobile app instead."
                  : ""
              }
              disableHoverListener={isEdit}
            >
              {/* Tooltip needs a wrapper span when the child is a
                  disabled button — MUI swallows hover events on the
                  button itself. */}
              <span>
                <Button
                  variant="contained"
                  disabled={
                    save.isPending ||
                    (isEdit ? !form.title : true /* see banner above */)
                  }
                  sx={primaryBtn}
                  onClick={() => save.mutate()}
                >
                  {isEdit ? "Save" : "Publish"}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              sx={greyBtn}
              onClick={() =>
                isEdit ? setForm(original) : navigate("/app/jobs")
              }
            >
              {isEdit ? "Reset" : "Cancel"}
            </Button>
          </Stack>
        </Paper>

        {/* Photos card — thumbnails of customer-uploaded request photos +
            engineer-uploaded completion photos. Admin can upload more
            request photos (e.g. follow-up reference images for a phoned-in
            job) via the Upload button; per-thumbnail × removes one. */}
        {isEdit && (
          <Paper elevation={0} sx={cardSx}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography sx={sectionHeading}>Photos</Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<UploadIcon />}
                disabled={uploading}
                sx={{
                  ...primaryBtn,
                  px: 2,
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? "Uploading…" : "Upload"}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  if (e.target.files) uploadFiles(e.target.files);
                }}
              />
            </Stack>
            {photos.length === 0 ? (
              <Typography
                sx={{
                  color: tokens.colors.admin.textBody,
                  fontSize: tokens.font.sizes.bodySm,
                }}
              >
                No photos uploaded for this job yet.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    md: "repeat(4, 1fr)",
                  },
                  gap: 2,
                }}
              >
                {photos.map((p) => (
                  <Box
                    key={p.id}
                    sx={{
                      border: `1px solid ${tokens.colors.admin.border}`,
                      borderRadius: 1,
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    {p.signed_url ? (
                      <a
                        href={p.signed_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Box
                          component="img"
                          src={p.signed_url}
                          alt={`${p.kind} photo`}
                          sx={{
                            width: "100%",
                            height: 160,
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      </a>
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: 160,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: tokens.colors.admin.textMuted,
                          fontSize: tokens.font.sizes.bodySm,
                        }}
                      >
                        Preview unavailable
                      </Box>
                    )}
                    <Chip
                      label={p.kind === "completion" ? "Completion" : "Request"}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        backgroundColor:
                          p.kind === "completion" ? "#5CB85C" : "#007bff",
                        color: "#fff",
                        fontSize: 11,
                        height: 20,
                      }}
                    />
                    <Tooltip title="Remove photo">
                      <IconButton
                        size="small"
                        aria-label="Remove photo"
                        disabled={deletePhoto.isPending}
                        onClick={() => deletePhoto.mutate(p.id)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "rgba(0,0,0,0.55)",
                          color: "#fff",
                          width: 24,
                          height: 24,
                          "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        )}

        {/* Engineer Locations card — admin-only audit data captured by the
            engineer mobile app. Hidden when no location data exists for
            this job (e.g. drafts, jobs cancelled before "I've arrived").
            All three location types (start, end, pings) link out to
            Google Maps for visual inspection. Not exposed to customer or
            engineer apps. */}
        {isEdit && hasAnyLocationData && (
          <Paper elevation={0} sx={cardSx}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography sx={sectionHeading}>Engineer Locations</Typography>
              <Chip
                size="small"
                label="Admin-only audit"
                sx={{
                  backgroundColor: "#FEF3C7",
                  color: "#B45309",
                  fontSize: 11,
                  height: 20,
                }}
              />
            </Stack>

            <Stack spacing={2}>
              <LocationRow
                icon={<StartIcon sx={{ color: "#22C55E" }} />}
                label="Service started"
                lat={startLat}
                lng={startLng}
                at={startAt}
                emptyText="Engineer hasn't tapped 'I've arrived' yet."
              />
              <LocationRow
                icon={<EndIcon sx={{ color: "#0065FF" }} />}
                label="Service completed"
                lat={endLat}
                lng={endLng}
                at={endAt}
                emptyText="Engineer hasn't submitted completion yet."
              />
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <PinDropIcon sx={{ color: "#6156D8", fontSize: 20 }} />
                  <Typography
                    sx={{
                      fontSize: tokens.font.sizes.bodySm,
                      fontWeight: 600,
                      color: tokens.colors.admin.textBody,
                    }}
                  >
                    Live location pings
                  </Typography>
                  <Chip
                    size="small"
                    label={`${locationPings.length}`}
                    sx={{ height: 18, fontSize: 11 }}
                  />
                </Stack>
                {locationPings.length === 0 ? (
                  <Typography
                    sx={{
                      color: tokens.colors.admin.textMuted,
                      fontSize: tokens.font.sizes.bodySm,
                      pl: 4,
                    }}
                  >
                    No live pings logged for this job.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      pl: 4,
                      maxHeight: 280,
                      overflowY: "auto",
                      border: `1px solid ${tokens.colors.admin.border}`,
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {locationPings.map((p) => (
                      <Stack
                        key={p.id}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          py: 0.5,
                          borderBottom: `1px dashed ${tokens.colors.admin.border}`,
                          "&:last-child": { borderBottom: "none" },
                          fontSize: tokens.font.sizes.bodySm,
                        }}
                      >
                        <Typography
                          sx={{
                            color: tokens.colors.admin.textMuted,
                            fontSize: 12,
                            minWidth: 130,
                            fontFamily: "monospace",
                          }}
                        >
                          {new Date(p.pinged_at).toLocaleString()}
                        </Typography>
                        <Typography
                          sx={{
                            color: tokens.colors.admin.textBody,
                            fontSize: 12,
                            fontFamily: "monospace",
                            flex: 1,
                          }}
                        >
                          {p.lat.toFixed(6)}, {p.lng.toFixed(6)}
                          {p.accuracy_m != null
                            ? ` · ±${Math.round(p.accuracy_m)}m`
                            : ""}
                        </Typography>
                        <Tooltip title="Open in Google Maps">
                          <IconButton
                            size="small"
                            component="a"
                            href={`https://www.google.com/maps?q=${p.lat},${p.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Open ping in Google Maps"
                          >
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ))}
                  </Box>
                )}
              </Box>
            </Stack>
          </Paper>
        )}
      </Stack>
      {ToastEl}
    </Box>
  );
}

// LocationRow — single fixed-point row (service started OR service ended).
// Renders the lat/lng + timestamp + a "Open in Google Maps" link, or an
// empty-state caption when the engineer hasn't reached that step yet.
function LocationRow({
  icon,
  label,
  lat,
  lng,
  at,
  emptyText,
}: {
  icon: React.ReactNode;
  label: string;
  lat: number | null;
  lng: number | null;
  at: string | null;
  emptyText: string;
}): React.ReactElement {
  const hasCoords = lat != null && lng != null;
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
      <Box sx={{ pt: 0.25 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontSize: tokens.font.sizes.bodySm,
            fontWeight: 600,
            color: tokens.colors.admin.textBody,
          }}
        >
          {label}
        </Typography>
        {hasCoords ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 0.25 }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontFamily: "monospace",
                color: tokens.colors.admin.textBody,
              }}
            >
              {lat.toFixed(6)}, {lng.toFixed(6)}
            </Typography>
            {at && (
              <Typography
                sx={{
                  fontSize: 12,
                  color: tokens.colors.admin.textMuted,
                }}
              >
                · {new Date(at).toLocaleString()}
              </Typography>
            )}
            <Tooltip title="Open in Google Maps">
              <IconButton
                size="small"
                component="a"
                href={`https://www.google.com/maps?q=${lat},${lng}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${label} in Google Maps`}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          <Typography
            sx={{
              fontSize: 12,
              color: tokens.colors.admin.textMuted,
              mt: 0.25,
            }}
          >
            {emptyText}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

// CustomerPicker — name-search autocomplete backed by /v1/admin/app-users.
// Stores the selected user's UUID in the form via onChange. Add-mode only;
// edit-mode shows the customer name as a locked TextField above.
interface CustomerOption {
  id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}
interface ListAppUsersRes {
  items: CustomerOption[];
}
function CustomerPicker(props: {
  value: string;
  onChange: (id: string) => void;
}) {
  const [input, setInput] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 250);
    return () => clearTimeout(t);
  }, [input]);

  const search = useQuery<ListAppUsersRes>({
    queryKey: ["app-users", "customer-search", debounced],
    queryFn: () =>
      apiFetch<ListAppUsersRes>(
        `/v1/admin/app-users?role=customer&limit=20&q=${encodeURIComponent(debounced)}`,
      ),
    enabled: debounced.length > 0,
  });

  const options = search.data?.items ?? [];
  const selected = options.find((o) => o.id === props.value) ?? null;

  return (
    <Autocomplete<CustomerOption>
      options={options}
      value={selected}
      filterOptions={(o) => o}
      loading={search.isFetching}
      getOptionLabel={(o) => {
        const name =
          [o.first_name, o.last_name].filter(Boolean).join(" ") ||
          o.display_name ||
          o.email ||
          o.id;
        return name;
      }}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => props.onChange(v?.id ?? "")}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          required
          label="Customer"
          helperText="Type to search customers by name"
          InputLabelProps={{ shrink: true }}
        />
      )}
    />
  );
}
